import React, { useState } from 'react';
import { contactInfo } from '../data/mock';
import { Phone, Mail, Instagram, Linkedin, Facebook, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    eventType: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock submission - frontend only
    console.log('Form submitted:', formData);
    
    toast.success('Thank you for your enquiry! We will get back to you soon.', {
      duration: 4000,
    });
    
    // Reset form
    setFormData({
      name: '',
      phone: '',
      email: '',
      eventType: '',
      message: ''
    });
  };

  return (
    <section id="contact" className="py-24 px-6 bg-black">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="heading-font text-5xl md:text-6xl font-bold text-white mb-4">
            Get In Touch
          </h2>
          <div className="w-24 h-1 gold-bg mx-auto mb-6"></div>
          <p className="body-font text-xl text-gray-300 max-w-3xl mx-auto">
            Let's create an unforgettable experience for your next conference
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-16">
          {/* Left: Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="heading-font text-3xl font-bold text-white mb-8">
                Contact Information
              </h3>
              
              {/* Phone */}
              <div className="mb-6">
                <p className="body-font text-sm text-gray-400 mb-3">Phone</p>
                {contactInfo.phones && contactInfo.phones.map((phone, idx) => (
                  <div key={idx} className="flex items-center gap-4 mb-3 group">
                    <div className="gold-bg w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Phone className="text-black" size={20} />
                    </div>
                    <div>
                      <p className="body-font text-xs text-gray-500">{phone.name}</p>
                      <a href={`tel:${phone.number}`} className="body-font text-lg text-white hover:text-yellow-400 transition-colors">
                        {phone.number}
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* Emails */}
              <div className="flex items-start gap-4 mb-6 group">
                <div className="gold-bg w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Mail className="text-black" size={20} />
                </div>
                <div>
                  <p className="body-font text-sm text-gray-400 mb-2">Email</p>
                  {contactInfo.emails.map((email, idx) => (
                    <a 
                      key={idx}
                      href={`mailto:${email}`} 
                      className="body-font text-lg text-white hover:text-yellow-400 transition-colors block"
                    >
                      {email}
                    </a>
                  ))}
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-10">
                <p className="body-font text-sm text-gray-400 mb-4">Follow Us</p>
                <div className="flex gap-4">
                  <a 
                    href={contactInfo.socialMedia.instagram} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gold-bg w-12 h-12 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                    aria-label="Instagram"
                  >
                    <Instagram className="text-black" size={20} />
                  </a>
                  <a 
                    href={contactInfo.socialMedia.youtube} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gold-bg w-12 h-12 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                    aria-label="YouTube"
                  >
                    <svg className="text-black w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                  <a 
                    href={contactInfo.socialMedia.linkedin} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gold-bg w-12 h-12 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="text-black" size={20} />
                  </a>
                  <a 
                    href={contactInfo.socialMedia.facebook} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gold-bg w-12 h-12 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                    aria-label="Facebook"
                  >
                    <Facebook className="text-black" size={20} />
                  </a>
                </div>
              </div>
            </div>

            {/* Quote Box */}
            <div className="bg-zinc-900 p-8 rounded-sm border border-yellow-600 mt-12">
              <p className="heading-font text-2xl gold-accent italic font-light leading-relaxed">
                "Every event is a canvas, every moment an opportunity to create magic."
              </p>
              <p className="body-font text-gray-400 mt-4">— Dr. Akanksha Mantri, Founder</p>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-zinc-900 p-10 rounded-sm border border-zinc-700">
            <h3 className="heading-font text-3xl font-bold text-white mb-6">
              Enquire Now
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="body-font text-sm text-gray-400 block mb-2">
                  Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-black border-zinc-700 text-white focus:border-yellow-600"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="phone" className="body-font text-sm text-gray-400 block mb-2">
                  Phone *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="bg-black border-zinc-700 text-white focus:border-yellow-600"
                  placeholder="Your contact number"
                />
              </div>

              <div>
                <label htmlFor="email" className="body-font text-sm text-gray-400 block mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-black border-zinc-700 text-white focus:border-yellow-600"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="eventType" className="body-font text-sm text-gray-400 block mb-2">
                  Event Type *
                </label>
                <Input
                  id="eventType"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleChange}
                  required
                  className="bg-black border-zinc-700 text-white focus:border-yellow-600"
                  placeholder="e.g., Medical Conference, Corporate Event"
                />
              </div>

              <div>
                <label htmlFor="message" className="body-font text-sm text-gray-400 block mb-2">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="bg-black border-zinc-700 text-white focus:border-yellow-600"
                  placeholder="Tell us about your event..."
                />
              </div>

              <Button 
                type="submit"
                className="w-full gold-bg text-black hover:bg-yellow-600 py-6 text-lg font-semibold body-font rounded-sm"
              >
                <Send className="mr-2" size={20} />
                Send Enquiry
              </Button>

              {/* WhatsApp Button */}
              <a
                href="https://wa.me/919167176555"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold body-font rounded-sm flex items-center justify-center gap-2 transition-colors mt-4"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Connect on WhatsApp
              </a>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
